import { useCallback, useEffect, useState } from 'react'
import {
  commercialBlocksApi,
  type CommercialBlock,
  type CommercialBlockReason,
  type CommercialBlocksResponse,
} from '../api/contacts'
import { Button, Callout, Card, CardContent, CardHeader, Dialog, FormGrid, List, ListItem, Select, StatusPill, TextField, useConfirm } from './ui'
import type { LoginResponse } from '../api/auth'
import { useFeatureFlag } from '../lib/featureFlags'

/**
 * F-154 (TASK-0408, REQ-006-011): blocchi commerciali di un cliente. Un responsabile (Manager o Admin) mette un
 * blocco con un motivo da elenco chiuso e una nota breve (al massimo 500 caratteri); finche' e' attivo, con
 * l'interruttore del server acceso, l'invio delle pratiche del cliente al backoffice e' fermato. Il blocco si toglie
 * e lo storico resta. Il componente e' autosufficiente: dietro il flag `clienti.blocchi_commerciali` (spento = non
 * disegna nulla) e, se il server risponde 403/404 (utente non staff, cliente non visibile), non mostra nulla.
 * Nessun importo. Componenti M3.
 */
const ROME = 'Europe/Rome'
const NOTE_MAX = 500
// Gli stessi ruoli della policy Manager del server (Manager e Admin): gli altri leggono soltanto.
const MANAGER_ROLES = ['Administrator', 'EqualAdmin', 'EqualDirettoreGenerale', 'EqualDirettoreCommerciale', 'EqualAreaManager']

function formatRome(iso: string): string {
  return new Date(iso).toLocaleString('it-IT', {
    timeZone: ROME, day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

export function CommercialBlockPanel({ contactId, auth }: { contactId: number; auth: LoginResponse }) {
  const canWrite = MANAGER_ROLES.includes(auth.role)
  const enabled = useFeatureFlag('clienti.blocchi_commerciali')
  const [data, setData] = useState<CommercialBlocksResponse | null>(null)
  const [hidden, setHidden] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [reason, setReason] = useState<CommercialBlockReason>('Moroso')
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [placing, setPlacing] = useState(false)
  const confirm = useConfirm()

  const load = useCallback(async () => {
    try {
      setData(await commercialBlocksApi.list(contactId))
      setHidden(false)
    } catch {
      // 403 (non staff) o 404 (cliente non visibile): il pannello non si mostra e non rivela altro.
      setHidden(true)
    }
  }, [contactId])

  useEffect(() => {
    if (!enabled || !Number.isFinite(contactId)) return
    void load()
  }, [enabled, contactId, load])

  if (!enabled || hidden || !data) return null

  const place = async () => {
    setBusy(true)
    setError(null)
    try {
      await commercialBlocksApi.place(contactId, { reasonCode: reason, note: note.trim() || undefined })
      setNote('')
      setPlacing(false)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossibile mettere il blocco')
    } finally {
      setBusy(false)
    }
  }

  const lift = async (b: CommercialBlock) => {
    if (!(await confirm({ title: 'Togli il blocco', message: `Il blocco «${b.reasonLabel}» verrà tolto; lo storico resta.`, confirmLabel: 'Togli il blocco' }))) return
    setBusy(true)
    setError(null)
    try {
      await commercialBlocksApi.lift(contactId, b.id)
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossibile togliere il blocco')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card variant="outlined">
      <CardHeader
        title="Blocchi commerciali"
        actions={
          <>
            {data.hasActiveBlock && <StatusPill tone="error">Blocco attivo</StatusPill>}
            {canWrite && (
              <Button type="button" variant="tonal" size="sm" disabled={busy} onClick={() => { setError(null); setPlacing(true) }}>
                Metti il blocco
              </Button>
            )}
          </>
        }
      />
      <CardContent>
        <div className="md-stack">
          {error && !placing && <Callout tone="error">{error}</Callout>}
          {data.items.length === 0 ? (
            <p className="md-typescale-body-medium text-muted m-0">Nessun blocco registrato per questo cliente.</p>
          ) : (
            <List aria-label="Blocchi commerciali">
              {data.items.map(b => (
                <ListItem
                  key={b.id}
                  overline={<StatusPill tone={b.isActive ? 'error' : 'neutral'}>{b.reasonLabel}</StatusPill>}
                  headline={`messo il ${formatRome(b.placedAt)}${b.liftedAt ? ` · tolto il ${formatRome(b.liftedAt)}` : ''}`}
                  supporting={b.note || undefined}
                  trailing={canWrite && b.isActive ? (
                    <Button type="button" variant="text" size="sm" disabled={busy} onClick={() => void lift(b)}>
                      Togli il blocco
                    </Button>
                  ) : undefined}
                />
              ))}
            </List>
          )}
          {canWrite && (
            <div className="md-typescale-body-small text-muted">
              Solo un responsabile puo' mettere o togliere un blocco. La nota non compare nelle verifiche d'invio.
            </div>
          )}
        </div>
      </CardContent>
      {canWrite && (
        <Dialog
          open={placing}
          onClose={() => setPlacing(false)}
          title="Metti il blocco"
          size="md"
          as="form"
          onSubmit={e => { e.preventDefault(); void place() }}
          actions={
            <>
              <Button type="button" variant="text" onClick={() => setPlacing(false)}>Annulla</Button>
              <Button type="submit" variant="text" disabled={busy}>Metti il blocco</Button>
            </>
          }
        >
          {error && <Callout tone="error">{error}</Callout>}
          <FormGrid>
            <Select id="commercial-block-reason" label="Motivo" value={reason}
              onChange={e => setReason(e.target.value as CommercialBlockReason)}>
              {data.reasons.map(r => <option key={r.code} value={r.code}>{r.label}</option>)}
            </Select>
            <TextField id="commercial-block-note" label="Nota (facoltativa)" value={note}
              maxLength={NOTE_MAX} onChange={e => setNote(e.target.value)} />
          </FormGrid>
        </Dialog>
      )}
    </Card>
  )
}
